import React from "react";
import './dshashmapscreen.css';
import {Paths} from "../../app/components/sidebar/Paths";
import LinkComponent, {ALinkTarget} from "../../app/components/link/LinkComponent";
import {CalloutComponent} from "../../app/components/callout/CalloutComponent";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {AccordionComponent} from "../../app/components/accordion/AccordionComponent";
import {AccordionItemComponent} from "../../app/components/accordion/AccordionItemComponent";
import {
    AccordionHeaderTextEmphasized,
    AccordionHeaderTextExtraTimeComplexity,
    AccordionHeaderTextTimeComplexityComponent
} from "../../app/components/accordion/AccordionHeaderTextComponent";
import {CarouselComponent} from "../../app/components/carousel/CarouselComponent";
import {CarouselItemComponent} from "../../app/components/carousel/CarouselItemComponent";
import {CodeComponent} from "../../app/components/code/CodeComponent";
import {HashMapInsertCabFullCaption} from "./assets/insert/cab/HashMapInsertCabSteps";
import HashMapRemoveLevel from "./assets/remove/level/HashMapRemoveLevel";
import HashMapRemoveAct from "./assets/remove/act/HashMapRemoveAct";
import HashMapRemoveCab from "./assets/remove/cab/HashMapRemoveCab";
import HashMapRemoveWho from "./assets/remove/who/HashMapRemoveWho";
import HashMapRemoveLap from "./assets/remove/lap/HashMapRemoveLap";
import HashMapRemoveUnhappyPath from "./assets/remove/HashMapRemoveUnhappyPath";
import HashMapInsertUnhappyPath from "./assets/insert/HashMapInsertUnhappyPath";
import HashMapInsertCat from "./assets/insert/cat/HashMapInsertCat";
import HashMapInsertCatTwo from "./assets/insert/cattwo/HashMapInsertCatTwo";
import HashMapInsertCab from "./assets/insert/cab/HashMapInsertCab";
import HashMapInsertTact from "./assets/insert/tact/HashMapInsertTact";
import HashMapInsertLevel from "./assets/insert/level/HashMapInsertLevel";
import HashMapInsertHow from "./assets/insert/how/HashMapInsertHow";
import HashMapInsertWho from "./assets/insert/who/HashMapInsertWho";
import {HashMapInsertCatFullCaption} from "./assets/insert/cat/HashMapInsertCatSteps";
import {HashMapInsertCatTwoFullCaption} from "./assets/insert/cattwo/HashMapInsertCatTwoSteps";
import {HashMapInsertTactFullCaption} from "./assets/insert/tact/HashMapInsertTactSteps";
import {HashMapInsertLevelFullCaption} from "./assets/insert/level/HashMapInsertLevelSteps";
import {HashMapInsertHowFullCaption} from "./assets/insert/how/HashMapInsertHowSteps";
import {HashMapInsertWhoFullCaption} from "./assets/insert/who/HashMapInsertWhoSteps";
import {HashMapRemoveLevelFullCaption} from "./assets/remove/level/HashMapRemoveLevelSteps";
import {HashMapRemoveActFullCaption} from "./assets/remove/act/HashMapRemoveActSteps";
import {HashMapRemoveCabFullCaption} from "./assets/remove/cab/HashMapRemoveCabSteps";
import {HashMapRemoveWhoFullCaption} from "./assets/remove/who/HashMapRemoveWhoSteps";
import {HashMapRemoveLapFullCaption} from "./assets/remove/lap/HashMapRemoveLapSteps";
import {HashMapInsertUnhappyFullCaption} from "./assets/insert/HashMapInsertUnhappySteps";
import {HashMapRemoveUnhappyFullCaption} from "./assets/remove/HashMapRemoveUnhappySteps";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {ProblemSetComponent} from "../components/problemset/ProblemSet";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";

export default function DSHashMapScreen() {
    return (<ScreenComponent>
        <Header/>
        <Conceptualizing/>
        <HashMapVHashSet/>
        <DesignKey/>
        <TimeComplexity/>
        <ProblemSet/>
    </ScreenComponent>)
}

function Header() {
    return (
        <GradientHeaderComponent title={Paths.dataStructures.nested.hashmap.name}
                                 tagline={Paths.dataStructures.nested.hashmap.tagline}/>
    )
}

function Conceptualizing() {
    const hashMapCharInt = "HashMap<Char, Int>"
    return (<H2SectionComponent header="Conceptualizing HashMaps" removeTopPadding={true}>
        <ul>
            <li>I like to think of HashMaps as mailboxes.</li>
            <li>The mailboxes don't have a specified order, because it's really the address on the front of the box that
                matters, aka the <CodeComponent className="codeKey">KEY</CodeComponent>.
            </li>
            <li>But also, no 2 people can have the same address, so each <CodeComponent
                className="codeKey">KEY</CodeComponent> is unique.
            </li>
            <li>In the <LinkComponent link={Paths.dataStructures.nested.hashset.path} target={ALinkTarget.internal}>last
                Study Guide</LinkComponent>, I went over HashSets which is the ability to store unique <CodeComponent
                className="codeValue">VALUES</CodeComponent>, but allow us to have constant lookup time through the
                introduction of a <CodeComponent className="codeHash">HASH</CodeComponent>. The big difference between
                HashSets and HashMaps is that in the map, we have an additional concept of a <CodeComponent
                    className="codeKey">KEY</CodeComponent>. Which, is actually a really nice thing because it allows us
                to sort things into buckets based off of those <CodeComponent className="codeKey">KEYS</CodeComponent>.
            </li>
            <li><CodeComponent className="codeKey">KEY</CodeComponent> - <CodeComponent
                className="codeValue">VALUE</CodeComponent> pairs are a great way to define a relationship between data.
            </li>
            <ul>
                <li>A common use case is counting how often a character appears in a string.
                    Your <CodeComponent>{hashMapCharInt}</CodeComponent> would be able to hold the unique
                    character <CodeComponent className="codeKey">KEYS</CodeComponent> and map them to a
                    count <CodeComponent className="codeValue">VALUE</CodeComponent> for how often it appears.
                </li>
                <li>Sometimes the relationship can be a bit more tricky to spot. For example, the question may be asking
                    you to group anagrams together. (See "Designing the Key" for more detail).
                </li>
            </ul>
        </ul>
    </H2SectionComponent>)
}

function HashMapVHashSet() {
    return (<H2SectionComponent header="HashMaps vs HashSets">
        <ul>
            <li>HashMaps are almost identical to HashSets in how they work, because of this, time complexities are also
                very similar.
            </li>
            <li>There are three (3) terms to know:</li>
            <div className="definitionGrid">
                <AlertComponent color={ThemeColor.Tertiary}>
                    <h6>KEY</h6>
                    <p>A unique value to associate with each <CodeComponent className="codeValue">VALUE</CodeComponent>
                    </p>
                </AlertComponent>
                <AlertComponent color={ThemeColor.Secondary}>
                    <h6>VALUE</h6>
                    <p>Data associated with each <CodeComponent className="codeKey">KEY</CodeComponent>.</p>
                </AlertComponent>
                <AlertComponent color={ThemeColor.Primary}>
                    <h6>HASH</h6>
                    <p>A generated index based off of the <CodeComponent className="codeKey">KEY</CodeComponent> to know
                        where the <CodeComponent className="codeKey">KEY</CodeComponent> - <CodeComponent
                            className="codeValue">VALUE</CodeComponent> pair is stored in memory.</p>
                </AlertComponent>
            </div>
            <li>The biggest difference is what is being stored at each <CodeComponent
                className="codeValue">VALUE</CodeComponent>.
            </li>
            <ul>
                <li>For HashSets, there is a <CodeComponent className="codeValue">VALUE</CodeComponent> stored at
                    each <CodeComponent className="codeHash">HASH</CodeComponent>.
                </li>
                <li>For HashMaps, there is an object stored at each <CodeComponent
                    className="codeHash">HASH</CodeComponent> that typically contains the <CodeComponent
                    className="codeKey">KEY</CodeComponent> and<CodeComponent
                    className="codeValue">VALUE</CodeComponent>.
                </li>
            </ul>
            <ul>
                <li>HashMaps can be implemented in several different ways, so each language can vary. In Java, they
                    actually store a node object that contains the <CodeComponent
                        className="codeHash">HASH</CodeComponent>, <CodeComponent
                        className="codeKey">KEY</CodeComponent>, <CodeComponent
                        className="codeValue">VALUE</CodeComponent>, and a reference to
                    the <CodeComponent>next</CodeComponent> node.
                </li>
                <li>The details don't matter too much, just understand the <CodeComponent
                    className="codeHash">HASH</CodeComponent> points to an object, not just the <CodeComponent
                    className="codeValue">VALUE</CodeComponent>.
                </li>
                <li>For the remainder of the post, I will be referring to this object as "<CodeComponent
                    className="codeKey">KEY</CodeComponent> - <CodeComponent
                    className="codeValue">VALUE</CodeComponent> object"
                </li>
            </ul>
        </ul>
    </H2SectionComponent>)
}

function DesignKey() {
    return (<H2SectionComponent header="Designing the Key">
        <ul>
            <li>As I mentioned before, <strong><CodeComponent className="codeKey">KEY</CodeComponent> - <CodeComponent
                className="codeValue">VALUE</CodeComponent> pairs are great for defining a relationship between
                data</strong>. Let's talk a closer look at this idea.
            </li>
            <li>Let's consider an example: You are given a list of strings, group the anagrams together.
                (Ex: <CodeComponent>["act", "lemon", "cat", "bat", "tab", "arm"]</CodeComponent> would
                return <CodeComponent>[["act", "cat"], ["lemon"], ["bat", "tab"], ["arm"]]</CodeComponent>)
            </li>
            <ul>
                <li>In this case, we would want to group "act" and "cat" together because they use the same 3 letters -
                    a, c, and t.
                </li>
                <li>"bat" and "tab" also have 3 letters, but they use a, b, and t.</li>
                <li>So how can we efficiently sort these words into buckets based on what letters are being used? We
                    could design a <CodeComponent className="codeKey">KEY</CodeComponent> that allows us to know what
                    letters are used for each bucket.
                </li>
                <ul>
                    <li><strong>Option 1:</strong> Sort the letters of the string - <CodeComponent>O(s log
                        s)</CodeComponent>, where s is the number of characters in the string. And then use
                        the <i>sorted</i> string as the <CodeComponent className="codeKey">KEY</CodeComponent>.
                    </li>
                    <ul>
                        <li>"act" -{">"} "act", "cat" -{">"} "act" | <CodeComponent
                            className="codeKey">KEY</CodeComponent> = "act"
                        </li>
                        <li>"bat" -{">"} "abt", "tab" -{">"} "abt" | <CodeComponent
                            className="codeKey">KEY</CodeComponent> = "abt"
                        </li>
                    </ul>
                    <li><strong>Option 2:</strong> Iterate over the string and the frequency of each character, storing
                        the result in HashMap - <CodeComponent>O(s)</CodeComponent>, where s is the number of characters
                        in the string. We could then convert they frequencies to a <CodeComponent
                            className="codeKey">KEY</CodeComponent>.
                    </li>
                    <ul>
                        <li>"act" -{">"} 1A1C1T, "cat" -{">"} 1C1A1T | <CodeComponent
                            className="codeKey">KEY</CodeComponent> = "act"
                        </li>
                        <li>"bat" -{">"} 1B1A1T, "tab" -{">"} 1T1A1B | <CodeComponent
                            className="codeKey">KEY</CodeComponent> = "tab"
                        </li>
                    </ul>
                </ul>
                <li>In this question we see that by designing the <CodeComponent
                    className="codeKey">KEY</CodeComponent> ourselves, we are able to quickly sort our strings into a
                    bucket to be returned later.
                </li>
            </ul>
            <li>As we can see, designing the <CodeComponent className="codeKey">KEY</CodeComponent> can have varying
                time complexities. In Option 1 above, we see <CodeComponent>O(s log s)</CodeComponent> time. In Option 2
                above, we see <CodeComponent>O(s)</CodeComponent> time complexity. In some cases, you may have constant
                time <CodeComponent>O(1)</CodeComponent>, if you are just grouping based on length of string, for
                example. There is no set time, it is heavily based on the type of question you are being asked, and what
                type of relationship you are trying to define.
            </li>
            <li><strong>****It is extremely important to separate the <CodeComponent
                className="codeKey">KEY</CodeComponent> generation from the <CodeComponent
                className="codeHash">HASH</CodeComponent> generation.</strong> While the <CodeComponent
                className="codeKey">KEY</CodeComponent> is <i>stored</i> in the HashMap, it is not <i>generated</i> in
                the HashMap. Because of this distinction, we do not consider the time complexity of the <CodeComponent
                    className="codeKey">KEY</CodeComponent> generation in our time complexities for HashMaps.
            </li>
        </ul>
    </H2SectionComponent>)
}

function TimeComplexity() {
    return (<H2SectionComponent header="What is the time complexity?">
        <CalloutComponent color={ThemeColor.Secondary}>
            <strong>Things to keep in mind...</strong>
            <ul>
                <li><strong>GenerateHash:</strong> <CodeComponent>O(1)</CodeComponent></li>
                <ul>
                    <li>The general principles of <CodeComponent className="codeHash">HASHING</CodeComponent> from
                        the <LinkComponent link={Paths.dataStructures.nested.hashset.path}
                                           target={ALinkTarget.internal}>HashSet Study Guide</LinkComponent> remain
                        true for HashMaps. The more collisions you have, the less efficient your code will be.
                    </li>
                    <li>The <CodeComponent className="codeHash">HASH</CodeComponent> is generated based on
                        the <CodeComponent className="codeKey">KEY</CodeComponent> value.
                    </li>
                </ul>
                <li>In most cases - the average case - you will see <CodeComponent>O(1)</CodeComponent> time for
                    operations, but when collisions increase, so does the time complexity, potentially up
                    to <CodeComponent>O(n)</CodeComponent> time.
                </li>
            </ul>
        </CalloutComponent>
        <AccordionComponent>
            <InsertTimeComplexity/>
            <InsertUnhappyTimeComplexity/>
            <RemoveTimeComplexity/>
            <RemoveUnhappyTimeComplexity/>
            <GetTimeComplexity/>
            <ContainsTimeComplexity/>
        </AccordionComponent>
    </H2SectionComponent>)
}

function InsertTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextExtraTimeComplexity title="Insert" timeComplexity="O(1)"
                                                            extraTimeComplexity="O(n)"/>}>

            <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
                <CarouselItemComponent><HashMapInsertCat/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertCatTwo/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertCab/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertTact/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertLevel/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertHow/></CarouselItemComponent>
                <CarouselItemComponent><HashMapInsertWho/></CarouselItemComponent>
            </CarouselComponent>

            {activeIndex === 0 && <HashMapInsertCatFullCaption/>}
            {activeIndex === 1 && <HashMapInsertCatTwoFullCaption/>}
            {activeIndex === 2 && <HashMapInsertCabFullCaption/>}
            {activeIndex === 3 && <HashMapInsertTactFullCaption/>}
            {activeIndex === 4 && <HashMapInsertLevelFullCaption/>}
            {activeIndex === 5 && <HashMapInsertHowFullCaption/>}
            {activeIndex === 6 && <HashMapInsertWhoFullCaption/>}
        </AccordionItemComponent>
    )
}

function InsertUnhappyTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextEmphasized title="Insert*" description="Visualizing the worst case of"
                                                   timeComplexity="O(n)"/>}>
            <p>As we saw above in our insert examples, our <CodeComponent>O(n)</CodeComponent> case happens when we need
                to re-size, and therefore, <CodeComponent className="codeHash">REHASH</CodeComponent> our HashMap.
                Hopefully, you've already picked up on what that looks like, but just to be sure, the following depicts
                the <CodeComponent>O(n)</CodeComponent> edge-case when <i>inserting</i> a <CodeComponent
                    className="codeValue">VALUE</CodeComponent> into our HashMap.</p>
            <HashMapInsertUnhappyPath/>
            <HashMapInsertUnhappyFullCaption/>
        </AccordionItemComponent>
    )
}

function RemoveTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextExtraTimeComplexity title="Remove"
                                                            timeComplexity="O(1)"
                                                            extraTimeComplexity="O(n)"/>}>

            <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
                <CarouselItemComponent><HashMapRemoveLevel/></CarouselItemComponent>
                <CarouselItemComponent><HashMapRemoveAct/></CarouselItemComponent>
                <CarouselItemComponent><HashMapRemoveCab/></CarouselItemComponent>
                <CarouselItemComponent><HashMapRemoveWho/></CarouselItemComponent>
                <CarouselItemComponent><HashMapRemoveLap/></CarouselItemComponent>
            </CarouselComponent>

            {activeIndex === 0 && <HashMapRemoveLevelFullCaption/>}
            {activeIndex === 1 && <HashMapRemoveActFullCaption/>}
            {activeIndex === 2 && <HashMapRemoveCabFullCaption/>}
            {activeIndex === 3 && <HashMapRemoveWhoFullCaption/>}
            {activeIndex === 4 && <HashMapRemoveLapFullCaption/>}
        </AccordionItemComponent>
    )
}

function RemoveUnhappyTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextEmphasized title="Remove*" description="Visualizing the worst case of"
                                                   timeComplexity="O(n)"/>}>
            <p>As we saw above in our remove examples, our <CodeComponent>O(n)</CodeComponent> case happens when every
                single <CodeComponent className="codeValue">VALUE</CodeComponent> collides with one another, and
                the <CodeComponent className="codeValue">VALUE</CodeComponent> we are looking for is the very last
                element in that sub-collection. Therefore, we must iterate over every single element to find
                the <CodeComponent className="codeValue">VALUE</CodeComponent> we are looking for.</p>
            <HashMapRemoveUnhappyPath/>
            <HashMapRemoveUnhappyFullCaption/>
        </AccordionItemComponent>
    )
}

function GetTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextExtraTimeComplexity title="Get" timeComplexity="O(1)"
                                                            extraTimeComplexity="O(n)"/>}>
            <ul>
                <li>Hopefully you are starting to see the pattern here.</li>
                <li>What does the get function do under the hood?</li>
                <ol>
                    <li>Figure out where the <CodeComponent className="codeValue">VALUE</CodeComponent> is we are trying
                        to get. To do this, we must generate a <CodeComponent
                            className="codeHash">HASH</CodeComponent> key. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Find the sub-collection at the <CodeComponent
                        className="codeHash">HASH</CodeComponent> index. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Iterate over the sub-collection to find the <CodeComponent
                        className="codeValue">VALUE</CodeComponent> we want to return.
                    </li>
                    <ul>
                        <li>On <strong>AVERAGE</strong>, with low-collision maps. <CodeComponent>O(1)</CodeComponent>
                        </li>
                        <li>At <strong>WORST</strong>, with high-collisions. <CodeComponent>O(n)</CodeComponent></li>
                    </ul>
                    <li>Return that <CodeComponent
                        className="codeValue">VALUE</CodeComponent>. <CodeComponent>O(1)</CodeComponent></li>
                </ol>
                <li>As you can see, in most cases, it is <CodeComponent>O(1)</CodeComponent>, and so that is recommended
                    when determining time complexity, however, it could be up to <CodeComponent>O(n)</CodeComponent> if
                    all of your <CodeComponent className="codeValue">VALUES</CodeComponent> are located at the
                    same <CodeComponent className="codeHash">HASH</CodeComponent>.
                </li>
            </ul>
        </AccordionItemComponent>
    )
}

function ContainsTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextTimeComplexityComponent title="Contains" timeComplexity="O(1)"/>}>
            <ul>
                <li>Patterns, patterns, patterns.</li>
                <li>What does the contains function do under the hood?</li>
                <ol>
                    <li>Figure out where the <CodeComponent className="codeValue">VALUE</CodeComponent> would be if it
                        was in the map. To do this, we must generate a <CodeComponent
                            className="codeHash">HASH</CodeComponent> key. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Find the sub-collection at the <CodeComponent
                        className="codeHash">HASH</CodeComponent> index. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Iterate over the sub-collection to find the <CodeComponent
                        className="codeValue">VALUE</CodeComponent> we are looking for exists.
                    </li>
                    <ul>
                        <li>On <strong>AVERAGE</strong>, with low-collision maps. <CodeComponent>O(1)</CodeComponent>
                        </li>
                        <li>At <strong>WORST</strong>, with high-collisions. <CodeComponent>O(n)</CodeComponent></li>
                    </ul>
                    <li>Return true if we found the <CodeComponent
                        className="codeValue">VALUE</CodeComponent>. <CodeComponent>O(1)</CodeComponent></li>
                </ol>
                <li>One last time, we see that in most cases, if HashMaps are used the way they are intended - with a
                    great <CodeComponent className="codeHash">HASH</CodeComponent> algorithm to minimize collisions, our
                    time complexity will be <CodeComponent>O(1)</CodeComponent> - which is the recommended time to use
                    in most cases. In worst case scenario, it can be <CodeComponent>O(n)</CodeComponent> however.
                </li>
            </ul>
        </AccordionItemComponent>
    )
}

function ProblemSet() {
    return (<ProblemSetComponent dataStructure={Paths.dataStructures.nested.hashmap.name}>
        <BasicProblems/>
        <AdvancedProblems/>
    </ProblemSetComponent>)
}

function BasicProblems() {
    return (<H3SectionComponent header="Basic Problems" removeTopPadding={true}>
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/contains-duplicate-ii/description/">Contains
                Duplicate II"</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/problems/group-anagrams/description/">Group
                Anagrams</LinkComponent> [Design the <CodeComponent className="codeKey">KEY</CodeComponent>]
            </li>
        </ul>
    </H3SectionComponent>)
}

function AdvancedProblems() {
    return (<H3SectionComponent header="Advanced Problems">
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/integer-to-roman/description/">Integer to
                Roman</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/problems/continuous-subarray-sum/description/">Continuous
                Subarray Sum"</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/problems/custom-sort-string/description/">Custom Sort
                String</LinkComponent></li>
        </ul>
    </H3SectionComponent>)
}
